import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { InputNumber } from 'primereact/inputnumber';
import { computersAPI, equipmentAPI } from '../services/api';
import { Computer, ComputerStatus, Equipment } from '../types';

const statusOptions: { label: string; value: ComputerStatus }[] = [
  { label: 'Aktivan', value: 'active' },
  { label: 'Neaktivan', value: 'inactive' },
  { label: 'Održavanje', value: 'maintenance' }
];

const statusSeverity: Record<ComputerStatus, 'success' | 'secondary' | 'warning'> = {
  active: 'success',
  inactive: 'secondary',
  maintenance: 'warning'
};

const ITComputers: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [computers, setComputers] = useState<Computer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [globalFilter, setGlobalFilter] = useState('');

  const [fromEquipmentVisible, setFromEquipmentVisible] = useState(false);
  const [equipmentList, setEquipmentList] = useState<Equipment[]>([]);
  const [fromEquipmentForm, setFromEquipmentForm] = useState<{ equipmentId: string | null; quantity: number }>({
    equipmentId: null,
    quantity: 1
  });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await computersAPI.getAll();
      setComputers(data);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError(e?.response?.data?.error || 'Greška pri učitavanju računara');
    } finally {
      setLoading(false);
    }
  };

  const loadEquipment = async () => {
    try {
      const data = await equipmentAPI.getAll();
      setEquipmentList(data);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri učitavanju opreme',
        life: 5000
      });
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openFromEquipment = async () => {
    if (!equipmentList.length) {
      await loadEquipment();
    }
    setFromEquipmentForm({ equipmentId: null, quantity: 1 });
    setFromEquipmentVisible(true);
  };

  const canCreateFromEquipment = useMemo(
    () => !!fromEquipmentForm.equipmentId && fromEquipmentForm.quantity > 0,
    [fromEquipmentForm.equipmentId, fromEquipmentForm.quantity]
  );

  const handleCreateFromEquipment = async () => {
    if (!canCreateFromEquipment || !fromEquipmentForm.equipmentId) return;

    setLoading(true);
    try {
      const res = await computersAPI.createFromEquipment({
        equipmentId: fromEquipmentForm.equipmentId,
        quantity: fromEquipmentForm.quantity
      });
      toast.current?.show({
        severity: 'success',
        summary: 'Uspešno',
        detail: res.message || 'Računari kreirani iz opreme',
        life: 3000
      });
      setFromEquipmentVisible(false);
      await load();
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri kreiranju računara iz opreme',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (row: Computer) => {
    confirmDialog({
      header: 'Potvrda brisanja',
      message: `Obrisati računar "${row.name}"?`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await computersAPI.delete(row._id);
          toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Računar obrisan', life: 2500 });
          await load();
        } catch (e: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Greška',
            detail: e?.response?.data?.error || 'Greška pri brisanju računara',
            life: 5000
          });
        }
      }
    });
  };

  const statusBody = (row: Computer) => (
    <Tag value={row.status === 'active' ? 'Aktivan' : row.status === 'inactive' ? 'Neaktivan' : 'Održavanje'} severity={statusSeverity[row.status]} />
  );

  const actionsBody = (row: Computer) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-share-alt"
        className="p-button-sm p-button-rounded p-button-text"
        tooltip="Podeli / IP dodela (uskoro)"
        disabled
      />
      <Button
        icon="pi pi-trash"
        className="p-button-sm p-button-rounded p-button-text p-button-danger"
        onClick={() => confirmDelete(row)}
        tooltip="Obriši"
      />
    </div>
  );

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="m-0">PC IP Upravljanje</h2>
        </div>
        <div className="flex gap-2">
          <Button label="Dodaj iz opreme" icon="pi pi-plus" onClick={openFromEquipment} />
          <Button label="" icon="pi pi-refresh" outlined onClick={load} aria-label="Osveži" />
        </div>
      </div>

      {error && <Message severity="error" text={error} className="mb-3" />}

      <Card>
        <div className="flex justify-content-end mb-3">
          <span className="p-input-icon-left" style={{ width: 'min(380px, 100%)' }}>
            <i className="pi pi-search" />
            <InputText
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Pretraga..."
              style={{ width: '100%' }}
            />
          </span>
        </div>

        <DataTable
          value={computers}
          loading={loading}
          paginator
          rows={10}
          emptyMessage="Nema servera"
          className="p-datatable-sm"
          globalFilter={globalFilter}
          globalFilterFields={['name', 'location', 'department', 'ipAddress', 'user']}
        >
          <Column field="name" header="Naziv" sortable />
          <Column field="location" header="Lokacija" sortable />
          <Column field="department" header="Odeljak" sortable />
          <Column field="ipAddress" header="IP Adresa" sortable />
          <Column field="user" header="Korisnik" sortable />
          <Column field="status" header="Status" body={statusBody} sortable style={{ width: '140px' }} />
          <Column header="Akcije" body={actionsBody} style={{ width: '150px' }} />
        </DataTable>
      </Card>

      <Dialog
        header="Dodaj računare iz opreme"
        visible={fromEquipmentVisible}
        onHide={() => setFromEquipmentVisible(false)}
        style={{ width: '700px' }}
        breakpoints={{ '960px': '95vw', '640px': '98vw' }}
        modal
        draggable={false}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Otkaži"
              className="p-button-secondary p-button-outlined"
              onClick={() => setFromEquipmentVisible(false)}
            />
            <Button
              label="Kreiraj računare"
              icon="pi pi-check"
              onClick={handleCreateFromEquipment}
              disabled={!canCreateFromEquipment}
              loading={loading}
            />
          </div>
        }
      >
        <div className="p-fluid formgrid grid">
          <div className="field col-12">
            <label>Oprema *</label>
            <Dropdown
              value={fromEquipmentForm.equipmentId}
              options={equipmentList}
              optionLabel="name"
              optionValue="_id"
              onChange={(e) => setFromEquipmentForm((p) => ({ ...p, equipmentId: e.value }))}
              placeholder="Izaberite opremu"
              filter
              showClear
              appendTo="self"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Količina</label>
            <InputNumber
              value={fromEquipmentForm.quantity}
              min={1}
              showButtons
              onValueChange={(e) => setFromEquipmentForm((p) => ({ ...p, quantity: e.value ?? 1 }))}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ITComputers;






